import { useState, type FormEvent } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { Panel } from '../components/Panel'
import { Field } from '../components/Field'
import { Button } from '../components/Button'
import { StatusDot } from '../components/StatusDot'
import { api, ApiError } from '../api/client'

export function LoginPage() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [err, setErr] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const nav = useNavigate()
  const loc = useLocation()
  const qc = useQueryClient()

  const from = (loc.state as { from?: string } | null)?.from ?? '/'

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setErr(null)
    setBusy(true)
    try {
      await api.post('/api/auth/login', { email, password })
      await qc.invalidateQueries({ queryKey: ['me'] })
      nav(from, { replace: true })
    } catch (e) {
      if (e instanceof ApiError) setErr(`AUTH FAIL · ${e.status}`)
      else setErr('AUTH FAIL · network')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-8">
      <div className="w-full max-w-[420px]">
        <div className="mb-10 text-center">
          <div className="font-serif text-[44px] leading-none text-paper tracking-tight">
            AAM<span className="text-amber-glow">Circ</span>
          </div>
          <div className="label mt-2">Subscription Ops Console · v0.1.0</div>
        </div>

        <Panel label="Operator Authentication" accent="amber">
          <form onSubmit={onSubmit} className="space-y-5">
            <Field
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="operator@cirrata.com"
              autoFocus
              required
            />
            <Field
              label="Passphrase"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            {err && (
              <div className="flex items-center gap-2 border border-radar-red/40 px-3 py-2 bg-radar-red/5">
                <StatusDot tone="err" pulse />
                <span className="font-mono text-[11px] uppercase tracking-label text-radar-red">
                  {err}
                </span>
              </div>
            )}
            <div className="pt-2">
              <Button type="submit" disabled={busy} size="lg" className="w-full justify-center">
                {busy ? 'Authenticating…' : 'Engage →'}
              </Button>
            </div>
          </form>
        </Panel>

        <div className="mt-8 space-y-1 text-center">
          <div className="label">Private system · Authorized access only</div>
          <div className="label text-paper-trace">All sessions are logged</div>
        </div>
      </div>
    </div>
  )
}
